// PendingEnrolmentModal.tsx
/**
 * Compact review modal for a pending enrolment, mirroring `Counselling/PendingCases`.
 *
 * Shows enough to decide (course, period, location, participants, progress) plus the note history,
 * with Accept / Reject in the footer.
 *
 * **Accept** moves the registration to Active via `facilitatorPortal.changeRegistrationStatus`.
 *
 * **Reject** must do two things: record why, and take this facilitator off the registration. Only
 * the first is possible with SDK 0.0.39 — there is no facilitator-portal equivalent of
 * `leaderPortal.declineCase`. The proposed `declineRegistration(registrationId, { reason })` is
 * specced in `_deploy/_newEndPoints/ac-courses-facilitator-decline.md` and is **feature-detected**
 * here, so the moment the SDK ships it this component switches to the atomic path with no edit.
 * Until then the reason is still saved and the user is told plainly that the office must action the
 * removal, rather than being shown a success message for something that only half happened.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  Alert, Badge, Button, Divider, Group, Modal, Progress, ScrollArea, Skeleton, Stack, Text,
  Textarea,
} from '@mantine/core';
import {
  IconAlertCircle, IconBooks, IconCalendarEvent, IconCheck, IconMapPin, IconNotes, IconUsers, IconX,
} from '@tabler/icons-react';
import {
  useFacilitatorPortalService, FacilitatorRegistrationView, RegistrationNote,
} from '@actschurch/ccms-sdk';
import { toast } from '@/components/ui/providers/AlertProvider';
import { brandBadgeStyle } from '@/utils/color';
import { badgeStyles } from '@/utils/tableStyles';
import { decodeNote, encodeNote } from '@/utils/noteCodec';
import { REGISTRATION_STATUS } from '@/pages/Grow/registrationStatusIds';

/** `yyyyMMddHHmmss` to `yyyy-MM-dd HH:mm`. */
function formatSDKDateTime(value: number | string | null | undefined): string {
  const raw = String(value ?? '');
  if (raw.length < 8) return '—';
  const date = `${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)}`;
  return raw.length >= 12 ? `${date} ${raw.slice(8, 10)}:${raw.slice(10, 12)}` : date;
}

/** Notes are stored as HTML; the history list only needs a readable one-liner. */
function noteToPlainText(note: string): string {
  const html = decodeNote(note ?? '');
  if (typeof document === 'undefined') return html;
  const el = document.createElement('div');
  el.innerHTML = html;
  return (el.textContent ?? '').trim();
}

function DetailRow({ icon, label, value }: { icon: React.ReactNode; label: string; value: React.ReactNode }) {
  return (
    <Group gap="sm" wrap="nowrap" align="flex-start">
      <div style={{ color: 'var(--mantine-color-dimmed)', marginTop: 2 }}>{icon}</div>
      <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
        <Text size="xs" c="dimmed">{label}</Text>
        <Text size="sm" fw={600} style={{ wordBreak: 'break-word' }}>{value}</Text>
      </Stack>
    </Group>
  );
}

interface PendingEnrolmentModalProps {
  registration: FacilitatorRegistrationView | null;
  opened: boolean;
  onClose: () => void;
  /** Called after a successful accept/decline so the parent can refresh its list. */
  onActioned: () => void;
  /** Open straight into the reject-reason form (used by the card's Reject button). */
  startInReject?: boolean;
}

export default function PendingEnrolmentModal({
  registration,
  opened,
  onClose,
  onActioned,
  startInReject = false,
}: PendingEnrolmentModalProps) {
  const facilitatorPortal = useFacilitatorPortalService();

  const [notes, setNotes] = useState<RegistrationNote[] | null>(null);
  const [notesError, setNotesError] = useState(false);

  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const [reasonTouched, setReasonTouched] = useState(false);
  const [busy, setBusy] = useState(false);

  const registrationId = registration?.registrationID;

  const fetchNotes = useCallback(async () => {
    if (!registrationId) return;
    setNotesError(false);
    try {
      const result = await facilitatorPortal.notes(registrationId);
      setNotes(Array.isArray(result) ? result : []);
    } catch (e) {
      console.error('Error fetching registration notes:', e);
      setNotesError(true);
      setNotes([]);
    }
  }, [facilitatorPortal, registrationId]);

  useEffect(() => {
    if (!opened) return;
    setNotes(null);
    setRejecting(startInReject);
    setReason('');
    setReasonTouched(false);
    fetchNotes();
  }, [opened, startInReject, fetchNotes]);

  const handleAccept = async () => {
    if (!registrationId) return;
    setBusy(true);
    try {
      await facilitatorPortal.changeRegistrationStatus(registrationId, REGISTRATION_STATUS.Active);
      toast.success('Enrolment accepted', 'The enrolment is now active.');
      onActioned();
      onClose();
    } catch (e) {
      console.error('Error accepting enrolment:', e);
      toast.error('Error', 'Could not accept the enrolment.');
    } finally {
      setBusy(false);
    }
  };

  const handleReject = async () => {
    if (!registrationId) return;

    const trimmed = reason.trim();
    if (!trimmed) {
      setReasonTouched(true);
      return;
    }

    setBusy(true);
    try {
      const portal = facilitatorPortal as any;

      // Preferred path: one call that records the reason and unassigns the facilitator.
      if (typeof portal.declineRegistration === 'function') {
        await portal.declineRegistration(registrationId, { reason: trimmed });
        toast.success(
          'Enrolment declined',
          'The reason was saved and you have been removed from this enrolment.',
        );
      } else {
        // Fallback: record the reason so the office has it, and say plainly what did not happen.
        await facilitatorPortal.addNote(registrationId, {
          registrationID: registrationId,
          note: encodeNote(`<p><strong>Enrolment declined.</strong> Reason: ${trimmed}</p>`),
          hideFromFacilitator: true,
        });
        toast.warning(
          'Reason recorded',
          'You are still assigned to this enrolment. The course office has to remove you, as the '
          + 'unassign endpoint is not available yet.',
        );
      }

      onActioned();
      onClose();
    } catch (e) {
      console.error('Error declining enrolment:', e);
      toast.error('Error', 'Could not decline the enrolment. The reason was not saved.');
    } finally {
      setBusy(false);
    }
  };

  const reasonInvalid = reasonTouched && !reason.trim();
  const participantNames = (registration?.participantNames ?? []).filter(Boolean).join(', ');

  return (
    <Modal
      opened={opened}
      onClose={busy ? () => {} : onClose}
      title={
        <Text fw={600}>
          {registration?.courseName
            || registration?.reference
            || (registrationId ? `Registration ${registrationId}` : 'Enrolment')}
        </Text>
      }
      size="lg"
      centered
      scrollAreaComponent={ScrollArea.Autosize}
    >
      {!registration ? null : (
        <Stack gap="md">
          {/* ── Summary ── */}
          <Group gap={8}>
            {registration.courseName && (
              <Badge variant="filled" styles={badgeStyles} style={brandBadgeStyle(registration.courseColor)}>
                {registration.courseName}
              </Badge>
            )}
            <Badge variant="filled" styles={badgeStyles} style={brandBadgeStyle(registration.statusColor)}>
              {registration.statusName ?? '—'}
            </Badge>
            <Badge variant="light" color={registration.isLead ? 'blue' : 'gray'}>
              {registration.isLead ? 'Lead' : 'Support'}
            </Badge>
            {registration.isRemote && (
              <Badge variant="light" color="grape">Remote</Badge>
            )}
          </Group>

          <Stack gap="sm">
            <DetailRow
              icon={<IconBooks size={18} />}
              label="Reference"
              value={registration.reference || `#${registration.registrationID}`}
            />
            <DetailRow
              icon={<IconCalendarEvent size={18} />}
              label="Period"
              value={registration.periodDisplay || '—'}
            />
            <DetailRow
              icon={<IconMapPin size={18} />}
              label="Location"
              value={registration.locationName || (registration.isRemote ? 'Remote' : '—')}
            />
            <DetailRow
              icon={<IconUsers size={18} />}
              label={`Participants (${registration.participantCount ?? 0})`}
              value={participantNames || 'None listed'}
            />
            <DetailRow
              icon={<IconCalendarEvent size={18} />}
              label="Next session"
              value={registration.nextSessionDisplay || 'No session scheduled'}
            />
          </Stack>

          {typeof registration.progressPercent === 'number' && (
            <>
              <Group justify="space-between" mb={4}>
                <Text size="xs" c="dimmed">Progress</Text>
                <Text size="xs" c="dimmed">{Math.round(registration.progressPercent)}%</Text>
              </Group>
              <Progress value={registration.progressPercent} radius="xl" />
            </>
          )}

          <Divider />

          {/* ── Note history ── */}
          <Group gap={6}>
            <IconNotes size={16} opacity={0.6} />
            <Text fw={600} size="sm">Notes</Text>
          </Group>

          {notes === null ? (
            <Stack gap="xs">
              <Skeleton height={12} width="80%" />
              <Skeleton height={12} width="60%" />
            </Stack>
          ) : notesError ? (
            <Text size="sm" c="dimmed">Could not load the note history.</Text>
          ) : notes.length === 0 ? (
            <Text size="sm" c="dimmed">No notes on this enrolment yet.</Text>
          ) : (
            <ScrollArea.Autosize mah={220}>
              <Stack gap="sm">
                {notes.map((n) => (
                  <Stack key={n.noteID} gap={2}>
                    <Text size="xs" c="dimmed">{formatSDKDateTime(n.entryDateTime)}</Text>
                    <Text size="sm">{noteToPlainText(n.note) || '—'}</Text>
                  </Stack>
                ))}
              </Stack>
            </ScrollArea.Autosize>
          )}

          {/* ── Reject reason ── */}
          {rejecting && (
            <>
              <Divider />
              <Alert
                icon={<IconAlertCircle size={16} />}
                color="red"
                variant="light"
                title="Why are you declining this enrolment?"
              >
                <Text size="xs" mb="xs">
                  The reason is saved to the enrolment for the course office. It is not shown to the
                  participants or to other facilitators.
                </Text>
                <Textarea
                  placeholder="e.g. I am already running two courses on those dates."
                  value={reason}
                  onChange={(e) => setReason(e.currentTarget.value)}
                  onBlur={() => setReasonTouched(true)}
                  error={reasonInvalid ? 'A reason is required to decline.' : undefined}
                  minRows={3}
                  autosize
                  data-autofocus
                />
              </Alert>
            </>
          )}

          <Divider />

          {/* ── Actions ── */}
          <Group justify="flex-end" gap="sm">
            {rejecting ? (
              <>
                <Button variant="subtle" onClick={() => setRejecting(false)} disabled={busy}>
                  Back
                </Button>
                <Button
                  color="red"
                  leftSection={<IconX size={16} />}
                  onClick={handleReject}
                  loading={busy}
                  disabled={!reason.trim()}
                >
                  Confirm Decline
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="light"
                  color="red"
                  leftSection={<IconX size={16} />}
                  onClick={() => setRejecting(true)}
                  disabled={busy}
                >
                  Reject
                </Button>
                <Button
                  color="green"
                  leftSection={<IconCheck size={16} />}
                  onClick={handleAccept}
                  loading={busy}
                >
                  Accept
                </Button>
              </>
            )}
          </Group>
        </Stack>
      )}
    </Modal>
  );
}
